import { FC, useCallback, useMemo, useRef, useState } from 'react';
import Style from './useStudyClosing.module.scss';
import clsx from 'clsx';
import { AppButton } from 'uikit/buttons';
import { ReactComponent as StopIcon } from 'assets/img/patient/stop_20.svg';
import { Tooltip } from 'uikit/tooltip/tooltip.component';
import { ConfirmResult, useConfirmationModal } from 'src/components/dialogModal/useConfirmationModal';
import { approveClosureStudy, closeStudy, revokeClosureApprovalStudy } from 'src/services/api/api-client/StudyClient';
import { useHasStudyClaims } from 'src/helpers/auth/auth-helper';
import { showServerErrorToast } from 'src/components/toast/toast-helper';
import { IStudyRoleDto, StudyRoleClaimValue } from 'src/services/api/api-client';
import { ReactComponent as DotIcon } from 'src/assets/img/formState/dot_16.svg';
import { ReactComponent as CheckIcon } from 'src/assets/img/formState/check_16.svg';
import { ReactComponent as ResetIcon } from 'src/assets/img/common/reset_24.svg';
import { useModal } from 'src/application/hooks/useModal';
import { DialogModal } from 'src/components/dialogModal/dialogModal.component';
import { AppTextInput } from 'uikit/inputs';
import { Trans, useTranslation } from 'react-i18next';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';
import { useStudy } from 'src/helpers/hooks/useStudy';
import { useGetStudyRolesQuery } from 'src/services/api/api-client/UserRolesQuery';
import { StudyInfoCard } from '../studyInfo/layoutComponents/StudyInfoPageLayout';
import { useGetDoctorStudyByIdQuery } from 'src/services/api/api-client/StudyQuery';
import { useRevalidator } from 'react-router-dom';

type CloseStudyConditionsType = {
  dataIsLoading: boolean;
  allPatientAreArchived: boolean;
  allIssuesAreClosed: boolean;
  closingIsApproved: boolean;
  studyIsClosed: boolean;
  hasLeastOnePermissions: boolean;
  hasOnlyApproveClosureStudyPermission: boolean;
  hasOnlyCloseStudyPermission: boolean;
};

export const useStudyClosing = () => {
  const { t } = useScopedTranslation('CloseStudy');
  const { id: studdyId } = useStudy() ?? {};
  const { revalidate } = useRevalidator();
  const { data: study, refetch } = useGetDoctorStudyByIdQuery(
    { id: studdyId! },
    { enabled: !!studdyId, keepPreviousData: false, refetchOnMount: 'always' },
  );

  const { hasOneOfStudyClaims, hasStudyClaim } = useHasStudyClaims();
  const approveConfirmationModal = useConfirmationModal();
  const revokeApprovalConfirmationModal = useConfirmationModal();
  const closeConfirmationModal = useCloseConfirmationModal();
  const rolesQuery = useGetStudyRolesQuery({ studyId: study?.id, includeForms: true }, { suspense: false });

  const closeStudyConditions: CloseStudyConditionsType = useMemo(
    () =>
      ({
        allPatientAreArchived: !!study?.allPatientsAreArchived,
        allIssuesAreClosed: !!study?.allIssuesAreClosed,
        studyIsClosed: !!study?.closedAt,
        closingIsApproved: !!study?.closingApprovedAt,
        dataIsLoading: rolesQuery.isInitialLoading,
        hasOnlyApproveClosureStudyPermission:
          hasStudyClaim(StudyRoleClaimValue.ApproveClosureStudy) && !hasStudyClaim(StudyRoleClaimValue.CloseStudy),
        hasOnlyCloseStudyPermission:
          !hasStudyClaim(StudyRoleClaimValue.ApproveClosureStudy) && hasStudyClaim(StudyRoleClaimValue.CloseStudy),
        hasLeastOnePermissions: hasOneOfStudyClaims(
          StudyRoleClaimValue.ApproveClosureStudy,
          StudyRoleClaimValue.CloseStudy,
        ),
      } satisfies CloseStudyConditionsType),
    [
      hasOneOfStudyClaims,
      hasStudyClaim,
      rolesQuery.isInitialLoading,
      study?.allIssuesAreClosed,
      study?.allPatientsAreArchived,
      study?.closedAt,
      study?.closingApprovedAt,
    ],
  );

  const buttonIsHidden = useMemo(
    () => closeStudyConditions.studyIsClosed || !closeStudyConditions.hasLeastOnePermissions,
    [closeStudyConditions],
  );

  const buttonIsDisabled = useMemo(
    () =>
      closeStudyConditions.studyIsClosed ||
      !closeStudyConditions.allPatientAreArchived ||
      !closeStudyConditions.allIssuesAreClosed ||
      closeStudyConditions.dataIsLoading ||
      (!closeStudyConditions.closingIsApproved && closeStudyConditions.hasOnlyCloseStudyPermission) ||
      (closeStudyConditions.closingIsApproved && closeStudyConditions.hasOnlyApproveClosureStudyPermission),

    [closeStudyConditions],
  );

  const approveHandler = useCallback(async () => {
    const result = await approveConfirmationModal.open({
      title: t('ApproveConfirmationModal.Title'),
      text: (
        <span>
          <Trans
            t={t}
            i18nKey={'ApproveConfirmationModal.Text'}
            values={{ studyNumber: study?.studyNumber, studyName: study?.title }}
            components={{
              Bold: <span className={Style.boldText} />,
              Red: <span className={Style.redText} />,
            }}
          />
        </span>
      ),
      okButtonColorSchema: 'destroy',
      okButtonText: t('ApproveConfirmationModal.OkButtonText'),
    });

    if (result !== ConfirmResult.Confirm) return;

    await approveClosureStudy(study!.id!);
    await refetch();
    revalidate();
  }, [approveConfirmationModal, refetch, revalidate, study, t]);

  const closeHandler = useCallback(async () => {
    const result = await closeConfirmationModal.open();

    if (result !== ConfirmResult.Confirm) return;

    await closeStudy(study!.id!);
    await refetch();
    revalidate();
  }, [closeConfirmationModal, refetch, revalidate, study]);

  const onCLickHandler = useCallback(async () => {
    try {
      if (hasStudyClaim(StudyRoleClaimValue.ApproveClosureStudy) && !study?.closingApprovedAt) {
        await approveHandler();
        return;
      }

      if (hasStudyClaim(StudyRoleClaimValue.CloseStudy) && !!study?.closingApprovedAt && !study?.closedAt) {
        await closeHandler();
        return;
      }
    } catch (err) {
      showServerErrorToast(err);
    }
  }, [approveHandler, closeHandler, hasStudyClaim, study?.closedAt, study?.closingApprovedAt]);

  const revokeApproval = useCallback(async () => {
    try {
      const result = await revokeApprovalConfirmationModal.open({
        okButtonColorSchema: 'destroy',
        title: t('RevokeApprovalConfirmationModal.Title'),
        text: t('RevokeApprovalConfirmationModal.Text'),
        okButtonText: t('RevokeApprovalConfirmationModal.OkButtonText'),
      });

      if (result !== ConfirmResult.Confirm) return;

      await revokeClosureApprovalStudy(study!.id);
      await refetch();
      revalidate();
    } catch (err) {
      showServerErrorToast(err);
    }
  }, [refetch, revalidate, revokeApprovalConfirmationModal, study, t]);

  const rolesCanApprove = useMemo(
    () =>
      rolesQuery.data?.filter((x: IStudyRoleDto) => x.claims.includes(StudyRoleClaimValue.ApproveClosureStudy)) ?? [],
    [rolesQuery.data],
  );

  const rolesCanCloseStudy = useMemo(
    () => rolesQuery.data?.filter((x: IStudyRoleDto) => x.claims.includes(StudyRoleClaimValue.CloseStudy)) ?? [],
    [rolesQuery.data],
  );

  return {
    buttonComponent: buttonIsHidden ? undefined : (
      <div>
        <Tooltip
          tooltipContent={
            <TooltipComponent
              buttonIsDisabled={buttonIsDisabled}
              closeStudyConditions={closeStudyConditions}
              rolesCanApprove={rolesCanApprove}
            />
          }
        >
          <AppButton
            variant={'icon-link'}
            colorSchema={'destroy'}
            Icon={StopIcon}
            onClick={onCLickHandler}
            disabled={!!buttonIsDisabled}
          />
        </Tooltip>
        {approveConfirmationModal.modal}
      </div>
    ),
    cardComponent: !closeStudyConditions.closingIsApproved ? undefined : (
      <StudyInfoCard className={clsx(Style.card, { [Style.center]: closeStudyConditions.studyIsClosed })}>
        <CardComponent
          closeStudyConditions={closeStudyConditions}
          rolesCanApprove={rolesCanApprove}
          rolesCanClose={rolesCanCloseStudy}
          onRevokeApproval={revokeApproval}
          onCloseStudy={onCLickHandler}
        />
        <div>
          {revokeApprovalConfirmationModal.modal}
          {closeConfirmationModal.modal}
        </div>
      </StudyInfoCard>
    ),
  };
};

const TooltipComponent: FC<{
  closeStudyConditions: CloseStudyConditionsType;
  buttonIsDisabled: boolean;
  rolesCanApprove: IStudyRoleDto[];
}> = ({ closeStudyConditions, buttonIsDisabled, rolesCanApprove }) => {
  const { t } = useScopedTranslation('CloseStudy.CloseCondition');

  if (!buttonIsDisabled) return <>{t('Tooltip')}</>;

  const roles = rolesCanApprove.map((x) => x.name).join(', ');

  return (
    <div className={Style.tooltipContainer}>
      <span>{t('Title')}</span>
      <TooltipRow
        state={closeStudyConditions.allPatientAreArchived ? 'done' : 'inProgress'}
        text={t('AllPatientArchived')}
      />
      <TooltipRow
        state={closeStudyConditions.allIssuesAreClosed ? 'done' : 'inProgress'}
        text={t('AllIssuesAreClosed')}
      />
      {closeStudyConditions.hasOnlyCloseStudyPermission && !closeStudyConditions.closingIsApproved && (
        <TooltipRow state={'inProgress'} text={t('StudyClosureApproved', { roles })} />
      )}
    </div>
  );
};

const CardComponent: FC<{
  closeStudyConditions: CloseStudyConditionsType;
  rolesCanApprove: IStudyRoleDto[];
  rolesCanClose: IStudyRoleDto[];
  onRevokeApproval: () => void;
  onCloseStudy: () => void;
}> = ({ closeStudyConditions, rolesCanApprove, rolesCanClose, onRevokeApproval, onCloseStudy }) => {
  const { t } = useScopedTranslation('CloseStudy');
  const { hasStudyClaim } = useHasStudyClaims();
  const study = useStudy();

  if (closeStudyConditions.studyIsClosed) {
    return (
      <div className={Style.closedCardContainer}>
        <div className={Style.title}>{t('ClosedBy')}</div>
        <div>{study?.closingApprovedBy?.fullName}</div>
        <div>{study?.closedBy?.fullName}</div>
      </div>
    );
  }

  return (
    <div className={Style.cardContainer}>
      <div className={Style.title}>{t('ClosingProgress')}</div>
      <div className={Style.rows}>
        <TooltipRow
          state={study?.closingApprovedBy ? 'done' : 'inProgress'}
          text={
            study?.closingApprovedBy ? study.closingApprovedBy.fullName : rolesCanApprove.map((x) => x.name).join(', ')
          }
          buttonComponent={
            !closeStudyConditions.studyIsClosed &&
            closeStudyConditions.closingIsApproved &&
            hasStudyClaim(StudyRoleClaimValue.ApproveClosureStudy) ? (
              <AppButton colorSchema={'primary'} variant={'icon-link'} Icon={ResetIcon} onClick={onRevokeApproval} />
            ) : undefined
          }
        />

        <TooltipRow
          state={study?.closedBy ? 'done' : 'inProgress'}
          text={study?.closedBy ? study.closedBy.fullName : rolesCanClose.map((x) => x.name).join(', ')}
          buttonComponent={
            !closeStudyConditions.studyIsClosed &&
            closeStudyConditions.closingIsApproved &&
            hasStudyClaim(StudyRoleClaimValue.CloseStudy) ? (
              <AppButton colorSchema={'destroy'} variant={'icon-link'} Icon={StopIcon} onClick={onCloseStudy} />
            ) : undefined
          }
        />
      </div>
    </div>
  );
};

const TooltipRow: FC<{
  state: 'done' | 'inProgress';
  text: string;
  buttonComponent?: JSX.Element;
}> = ({ state, text, buttonComponent }) => {
  return (
    <div className={clsx(Style.textWithIcon, { [Style.green]: state === 'done' })}>
      {state === 'done' ? <CheckIcon /> : <DotIcon />}
      <div>{text}</div>
      {buttonComponent}
    </div>
  );
};

const useCloseConfirmationModal = () => {
  const { t: commonTranslation } = useTranslation();
  const { t } = useScopedTranslation('CloseStudy');
  const modal = useModal('CLOSED');
  const promiseRef = useRef<(test: ConfirmResult) => void>();
  const study = useStudy();
  const [input, setInput] = useState('');

  const closeHandler = useCallback(() => {
    promiseRef?.current?.(ConfirmResult.Closed);
    modal.closeModal();
  }, [modal]);

  const okClickHandler = useCallback(() => {
    promiseRef?.current?.(ConfirmResult.Confirm);
    modal.closeModal();
  }, [modal]);

  const cancelClickHandler = useCallback(() => {
    promiseRef?.current?.(ConfirmResult.Cancel);
    modal.closeModal();
  }, [modal]);

  return {
    open: (): Promise<ConfirmResult> => {
      modal.openModal();

      return new Promise<ConfirmResult>((resolve) => {
        promiseRef.current = resolve;
      });
    },
    modal: (
      <DialogModal
        visible={modal.visible}
        onHide={closeHandler}
        title={t('CloseConfirmationModal.Title')}
        containerClassName={Style.confirmationModalContainer}
        footer={{
          leftButton: {
            text: commonTranslation('Common_Cancel'),
            onClick: cancelClickHandler,
          },
          rightButton: {
            colorSchema: 'destroy',
            text: t('CloseConfirmationModal.OkButtonText'),
            onClick: okClickHandler,
            disabled: input !== study?.studyNumber,
          },
        }}
      >
        <span>
          <Trans
            t={t}
            i18nKey={'CloseConfirmationModal.Text'}
            values={{ studyNumber: study?.studyNumber, studyName: study?.title }}
            components={{
              Bold: <span className={Style.boldText} />,
              Red: <span className={Style.redText} />,
            }}
          />
          <AppTextInput
            className={Style.modalInput}
            placeholder={t('CloseConfirmationModal.InputPlaceholder')}
            onChange={(e) => setInput(e.target.value)}
          />
        </span>
      </DialogModal>
    ),
  };
};
